import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert } from './Alert';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { Field } from './Field';
import { SelectField } from './SelectField';
import { api, queryKeys } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isApiCode, messageForError } from '../lib/errors';

/** Account deletion: password confirmation, plus an ownership-transfer picker when required. */
export function DeleteAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { auth, setMe } = useAuth();
  const soleOwner = auth.status === 'authed' && auth.role === 'owner';
  const members = useQuery({
    queryKey: queryKeys.members,
    queryFn: () => api.households.members.list(),
    enabled: open && soleOwner,
  });
  const [needTransfer, setNeedTransfer] = useState(false);
  const del = useMutation({
    mutationFn: (input: { password: string; transferOwnershipTo?: string }) =>
      api.auth.deleteAccount(input),
    onSuccess: () => {
      setMe(null);
      window.location.assign('/login?deleted=1');
    },
    onError: (err) => {
      if (isApiCode(err, 'OWNERSHIP_TRANSFER_REQUIRED')) setNeedTransfer(true);
    },
  });
  const others = (members.data?.members ?? []).filter(
    (m) => auth.status === 'authed' && m.user.id !== auth.user.id,
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const password = String(fd.get('password') ?? '');
    const transferOwnershipTo = String(fd.get('transferOwnershipTo') ?? '');
    del.mutate({ password, ...(transferOwnershipTo ? { transferOwnershipTo } : {}) });
  }

  return (
    <Dialog open={open} onClose={onClose} title="Delete your account?">
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          This cannot be undone. Your recipes and plans in households where you are the only member
          are removed too.
        </p>
        <Field
          id="delete-password"
          name="password"
          label="Your password"
          type="password"
          autoComplete="current-password"
          required
        />
        {(needTransfer || (soleOwner && others.length > 0)) && others.length > 0 ? (
          <SelectField
            id="transferOwnershipTo"
            name="transferOwnershipTo"
            label="Hand household ownership to"
            options={[
              { value: '', label: 'Choose a member…' },
              ...others.map((m) => ({
                value: m.user.id,
                label: `${m.user.displayName} (${m.user.email})`,
              })),
            ]}
          />
        ) : null}
        {del.isError ? <Alert tone="error">{messageForError(del.error)}</Alert> : null}
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={del.isPending}>
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
